import { useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { DayPicker } from "react-day-picker";
import { format, isValid, parseISO } from "date-fns";
import { CaseReviewIcon } from "../CaseReviewIcon";
import { CASE_DATE_DISPLAY_FORMAT } from "../../../utils/caseReviewDateTime";

interface Props {
  id: string; value: string | null; required: boolean; invalid: boolean; describedBy?: string;
  onChange: (value: string | null) => void;
}

const ISO = "yyyy-MM-dd";
const PLACEHOLDER = "Pick a date";

function parse(value: string | null): Date | undefined {
  if (!value) return undefined;
  const date = parseISO(value);
  return isValid(date) ? date : undefined;
}

/** Date picker: a mono trigger showing the date, a calendar in a popover. Values stay ISO strings. */
export function CaseFieldDatePicker({ id, value, required, invalid, describedBy, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const date = parse(value);
  return <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
    <PopoverPrimitive.Trigger id={id} className="cr-select-trigger cr-date-trigger" aria-required={required} aria-invalid={invalid} aria-describedby={describedBy} data-empty={!date}>
      <span className="cr-select-value cr-mono-value">{date ? format(date, CASE_DATE_DISPLAY_FORMAT) : value || PLACEHOLDER}</span>
      <span className="cr-select-chevron"><CaseReviewIcon name="calendar" size={13} strokeWidth={2} /></span>
    </PopoverPrimitive.Trigger>
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content className="cr-portal cr-calendar-pop" sideOffset={4} align="start">
        <DayPicker mode="single" selected={date} defaultMonth={date} showOutsideDays weekStartsOn={1}
          onSelect={(next) => { onChange(next ? format(next, ISO) : null); setOpen(false); }}
          className="cr-calendar"
          classNames={{ months: "cr-cal-months", month: "cr-cal-month", caption: "cr-cal-caption", caption_label: "cr-cal-label", nav: "cr-cal-nav", nav_button: "cr-cal-nav-button",
            nav_button_previous: "cr-cal-prev", nav_button_next: "cr-cal-next", table: "cr-cal-table", head_row: "cr-cal-head-row", head_cell: "cr-cal-head-cell", row: "cr-cal-row",
            cell: "cr-cal-cell", day: "cr-cal-day", day_selected: "cr-cal-day-selected", day_today: "cr-cal-day-today", day_outside: "cr-cal-day-outside", day_disabled: "cr-cal-day-disabled", day_hidden: "cr-cal-day-hidden" }}
          components={{ IconLeft: () => <CaseReviewIcon name="chevronLeft" size={14} strokeWidth={2.2} />, IconRight: () => <CaseReviewIcon name="chevronRight" size={14} strokeWidth={2.2} /> }} />
        <div className="cr-calendar-foot">
          <button type="button" className="cr-calendar-action" onClick={() => { onChange(format(new Date(), ISO)); setOpen(false); }}>Today</button>
          <span className="cr-toolbar-spacer" />
          {date && <button type="button" className="cr-calendar-action" onClick={() => { onChange(null); setOpen(false); }}>Clear</button>}
        </div>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  </PopoverPrimitive.Root>;
}
